import { MAX_TIER } from '../config';

/** Minimal contract for a fruit-as-collision-candidate. The real `Fruit`
 *  class exposes the same fields via `entities/Fruit.ts`. */
export interface MergeCandidate {
  id: number;
  tier: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** Mutated when the body participates in a merge this tick. */
  merging: boolean;
}

export interface MergeEvent {
  newTier: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  removedIds: number[];
}

const VELOCITY_DAMP = 0.7;

/**
 * Handle a batch of collision pairs reported by Matter in one tick.
 * Pure function: caller passes already-resolved fruit candidates, we decide
 * which pairs merge, fire `onMerge` for each, and mutate `candidate.merging`
 * so subsequent pairs in the same batch skip already-merged bodies.
 */
export function handleCollisionPairs(
  pairs: ReadonlyArray<readonly [MergeCandidate, MergeCandidate]>,
  onMerge: (event: MergeEvent) => void,
): void {
  for (const [a, b] of pairs) {
    if (a.merging || b.merging) continue;
    if (a.tier !== b.tier) continue;
    if (a.tier >= MAX_TIER) continue;

    a.merging = true;
    b.merging = true;
    const newTier = a.tier + 1;
    onMerge({
      newTier,
      x: (a.x + b.x) / 2,
      y: (a.y + b.y) / 2,
      vx: ((a.vx + b.vx) / 2) * VELOCITY_DAMP,
      vy: ((a.vy + b.vy) / 2) * VELOCITY_DAMP,
      removedIds: [a.id, b.id],
    });
  }
}
